import React, { FC } from "react";
import { observer } from "mobx-react";
import {
  CustomerFacingCheckOutItemModel,
  getPurchaseItemsPriceDetails,
  MerchantAccountWithPaySettingsModel,
  PurchaseCheckOutAddOnDetails,
  PurchaseItemsPriceModel,
  PurchasePaymentModel,
} from "../models";
import { CustomLine } from "../styles";
import {
  CollapsiblePanel,
  CustomLabel,
  FlexContainer,
} from "@mindmemobile/mm-shared-global";
import { AccountSettingsStore } from "../store";
import { CheckOutPromoCodeSelectionType } from "../enums";

type IProps = {
  items: CustomerFacingCheckOutItemModel[];
  addOns: PurchaseCheckOutAddOnDetails[];
  merchantAccount: MerchantAccountWithPaySettingsModel;
  includeSurcharge?: boolean;
  paymentSectionData: PurchasePaymentModel;
  promoCodeSelectionType: keyof typeof CheckOutPromoCodeSelectionType;
};

const PurchasePaymentSummarySection: FC<IProps> = ({
  items,
  addOns,
  merchantAccount,
  includeSurcharge,
  paymentSectionData,
  promoCodeSelectionType,
}) => {
  const clonedItems = items.map(
    (item) =>
      new CustomerFacingCheckOutItemModel({
        ...item,
        selectedVariantIndex:
          item.selectedVariantIndex === -1 ? 0 : item.selectedVariantIndex,
      })
  );

  const summaryDetailsToCalculateTotalPrice = getPurchaseItemsPriceDetails(
    clonedItems,
    addOns,
    0,
    undefined
  );

  const totalPriceWithooutSurchargeDiscount =
    summaryDetailsToCalculateTotalPrice
      .map((s) => s.subTotal)
      .reduce((a, b) => a + b, 0);

  const minimumPurchase = paymentSectionData.promotionRelatedData
    ? paymentSectionData.promotionRelatedData.minimumPurchase || 0
    : 0;

  const promoData =
    promoCodeSelectionType === "ApplyCode"
      ? totalPriceWithooutSurchargeDiscount > minimumPurchase
        ? paymentSectionData.promotionRelatedData
        : undefined
      : paymentSectionData.promotionRelatedData;

  const includeSurchargeOnApplyPromo = paymentSectionData.promotionRelatedData
    ? !paymentSectionData.promotionRelatedData.waiveSurcharge
    : true;

  const summaryDetails = getPurchaseItemsPriceDetails(
    clonedItems,
    addOns,
    includeSurcharge && includeSurchargeOnApplyPromo
      ? merchantAccount.surchargePercentage()
      : 0,
    promoData || undefined
  );

  const renderListItem = (
    summaryDetails: PurchaseItemsPriceModel,
    index: number
  ) => {
    const totalPrice = AccountSettingsStore.formatAmount(
      summaryDetails.totalPrice,
      true
    );
    return (
      <FlexContainer
        key={summaryDetails.title + index}
        fxLayout="column"
        width={"100%"}
        margin="0px"
        // bgColor={summaryDetails.expanded ? '#F8F8F8' : ''}
        // padding={summaryDetails.expanded ? '30px 10px' : ''}
      >
        <CollapsiblePanel
          isOpened={true}
          titleAsNode={summaryDetails.titleAsNode}
          title={summaryDetails.title}
          className="payment-collapsed-panel"
          customCollapsedData={true}
          price={totalPrice}
        >
          <FlexContainer justifyContent="space-between">
            <CustomLabel>
              <span className="semi-bold">Sub-Total</span>
            </CustomLabel>
            <CustomLabel>
              <span className="semi-bold">
                $
                {AccountSettingsStore.formatAmount(
                  summaryDetails.subTotal,
                  true
                )}
              </span>
            </CustomLabel>
          </FlexContainer>
          <CustomLine width="100%" height="1px" bgColor="#ddd" />
          <FlexContainer fxLayout="column">
            <FlexContainer justifyContent="space-between">
              <CustomLabel>Discount</CustomLabel>
              <CustomLabel>
                {`($${AccountSettingsStore.formatAmount(summaryDetails.discount, true)})`}
              </CustomLabel>
            </FlexContainer>

            <FlexContainer justifyContent="space-between">
              <CustomLabel>Shipping</CustomLabel>
              <CustomLabel>
                $
                {AccountSettingsStore.formatAmount(
                  summaryDetails.shipping,
                  true
                )}
              </CustomLabel>
            </FlexContainer>
            <FlexContainer justifyContent="space-between">
              <CustomLabel>Tax</CustomLabel>
              <CustomLabel>
                ${AccountSettingsStore.formatAmount(summaryDetails.tax, true)}
              </CustomLabel>
            </FlexContainer>
          </FlexContainer>
          <FlexContainer justifyContent="space-between">
            <CustomLabel>Surcharge</CustomLabel>
            <CustomLabel>
              $
              {AccountSettingsStore.formatAmount(
                summaryDetails.surcharge,
                true
              )}
            </CustomLabel>
          </FlexContainer>
          <CustomLine width="100%" height="1px" bgColor="#ddd" />{" "}
          <FlexContainer
            justifyContent="space-between"
            margin="0px 0px 10px 0px"
          >
            <CustomLabel>
              {" "}
              <span className="semi-bold">Total</span>
            </CustomLabel>
            <CustomLabel>
              {" "}
              <span className="semi-bold">
                $
                {AccountSettingsStore.formatAmount(
                  summaryDetails.totalPrice,
                  true
                )}
              </span>
            </CustomLabel>
          </FlexContainer>
        </CollapsiblePanel>
      </FlexContainer>
    );
  };

  return (
    <FlexContainer fxLayout="column" margin="40px 0px 26px 0px">
      <CustomLabel
        fontSize="18px"
        lineHeight="34px"
        fontWeight="bold"
        margin={"0px 0px 40px"}
        color="#000"
      >
        SUMMARY
      </CustomLabel>
      <FlexContainer fxLayout="column" width={"100%"} spacing={20}>
        {summaryDetails.map((item, index) => renderListItem(item, index))}
      </FlexContainer>
    </FlexContainer>
  );
};

export default observer(PurchasePaymentSummarySection);